import React, { useState, useEffect, useReducer, useCallback, useContext } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import { Chip } from "@material-ui/core";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import TagPhraseModal from "../../components/TagPhraseModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";

const reducer = (state, action) => {
  if (action.type === "LOAD") {
    const items = action.payload;
    const newItems = [];
    items.forEach(item => {
      const idx = state.findIndex(s => s.id === item.id);
      if (idx !== -1) {
        state[idx] = item;
      } else {
        newItems.push(item);
      }
    });
    return [...state, ...newItems];
  }

  if (action.type === "UPDATE") {
    const item = action.payload;
    const idx = state.findIndex(s => s.id === item.id);
    if (idx !== -1) {
      state[idx] = item;
      return [...state];
    }
    return [item, ...state];
  }

  if (action.type === "DELETE") {
    const id = action.payload;
    const idx = state.findIndex(s => s.id === id);
    if (idx !== -1) {
      state.splice(idx, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles
  },
  tagChip: {
    color: "#FFF",
    fontWeight: 600,
    borderRadius: 3,
    fontSize: "0.8em"
  }
}));

const TagPhrases = () => {
  const classes = useStyles();

  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);

  const isAdmin = user?.profile === "admin";

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selected, setSelected] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [phrases, dispatch] = useReducer(reducer, []);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchPhrases = useCallback(async () => {
    try {
      const { data } = await api.get("/tag-phrases", {
        params: { searchParam, pageNumber }
      });
      dispatch({ type: "LOAD", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  }, [searchParam, pageNumber]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchPhrases();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, fetchPhrases]);

  useEffect(() => {
    const socket = socketManager.getSocket(user.companyId);

    // Nome do evento igual ao emitido em TagPhraseController.emit.
    socket.on(`company-${user.companyId}-tagphrase`, data => {
      if (data.action === "create" || data.action === "update") {
        dispatch({ type: "UPDATE", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE", payload: +data.id });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [socketManager, user]);

  const handleOpenModal = () => {
    setSelected(null);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelected(null);
    setModalOpen(false);
  };

  const handleSearch = event => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEdit = phrase => {
    setSelected(phrase);
    setModalOpen(true);
  };

  const handleDelete = async id => {
    try {
      await api.delete(`/tag-phrases/${id}`);
      toast.success(i18n.t("tagPhrases.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeleting(null);
    setSearchParam("");
    dispatch({ type: "RESET" });
    setPageNumber(1);
    await fetchPhrases();
  };

  const loadMore = () => {
    setPageNumber(prev => prev + 1);
  };

  const handleScroll = e => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={i18n.t("tagPhrases.confirmationModal.deleteTitle")}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDelete(deleting.id)}
      >
        {i18n.t("tagPhrases.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <TagPhraseModal
        open={modalOpen}
        onClose={handleCloseModal}
        reload={fetchPhrases}
        tagPhraseId={selected && selected.id}
      />
      <MainHeader>
        <Title>{i18n.t("tagPhrases.title")}</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("tagPhrases.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              )
            }}
          />
          {isAdmin && (
            <Button variant="contained" color="primary" onClick={handleOpenModal}>
              {i18n.t("common.add")}
            </Button>
          )}
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">{i18n.t("tagPhrases.fields.phrase")}</TableCell>
              <TableCell align="center">{i18n.t("tagPhrases.fields.tag")}</TableCell>
              <TableCell align="center">{i18n.t("tagPhrases.fields.match")}</TableCell>
              <TableCell align="center">{i18n.t("common.status")}</TableCell>
              {isAdmin && <TableCell align="center">{i18n.t("common.actions")}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {phrases.map(phrase => (
                <TableRow key={phrase.id}>
                  <TableCell align="center">{phrase.phrase}</TableCell>
                  <TableCell align="center">
                    {phrase.tag ? (
                      <Chip
                        size="small"
                        className={classes.tagChip}
                        style={{ background: phrase.tag.color || "#eee" }}
                        label={(phrase.tag.name || "").toUpperCase()}
                      />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        phrase.exactMatch
                          ? i18n.t("tagPhrases.match.exact")
                          : i18n.t("tagPhrases.match.contains")
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={phrase.active ? i18n.t("tagPhrases.status.active") : i18n.t("tagPhrases.status.inactive")}
                      color={phrase.active ? "primary" : "default"}
                    />
                  </TableCell>
                  {isAdmin && (
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleEdit(phrase)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setConfirmModalOpen(true);
                          setDeleting(phrase);
                        }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={isAdmin ? 5 : 4} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default TagPhrases;
